/* eslint-disable max-len */
import React, { useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import { toast } from 'react-toastify'

import { Box, Button, ListItem, SidebarNoticeBox, Text } from '@campaignhub/suit-theme'
import { useThunkDispatch } from '@campaignhub/react-hooks'

import PageContext from '@contexts/pageContext'
import useServiceJob from '@hooks/useServiceJob'
import useServiceJobDetail from '@hooks/useServiceJobDetail'
import useUploadResource from '@hooks/useUploadResource'
import useUploadMultipleResource from '@hooks/useUploadMultipleResource'
import useCurrentUser from '@hooks/useCurrentUser'
import useServiceJobSetAvailable from '@hooks/useServiceJobSetAvailable'
import useServiceJobPushRaw from '@hooks/useServiceJobPushRaw'
import useServiceJobSendToEsoft from '@hooks/useServiceJobSendToEsoft'

import criticalPromptServices from '@enums/criticalPromptServices'
import userTypes from '@enums/userTypes'
import prestigeServices from '@enums/prestigeServices'
import portraitServices from '@enums/portraitServices'
import droneServices from '@enums/droneServices'
import { patchServiceJobCritical } from '@redux/modules/serviceJob'
import promptCriticalIssue from '@functions/promptCriticalIssue'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCloudUpload, faPaperPlane } from '@fortawesome/pro-light-svg-icons'

const assignServiceJobStatus = (serviceJobParam, createFn) => {
  createFn(serviceJobParam).then(({ errors, success }) => {
    if (!success && errors) {
      toast.warning(errors[0])
    }
  })
}

const upsertServiceJobDetail = (serviceJobDetail, createFn) => {
  createFn(serviceJobDetail).then(({ success, errors }) => {
    if (!success) toast.warning(errors[0])
  })
}

const Footer = (props) => {
  const { callbacks: { toggleCheckActionStatus }, isMissingResources, selectedIds, disableUpload } = props

  const { serviceJob } = useContext(PageContext)
  const dispatch = useThunkDispatch()

  const useUploadResourcePayload = useUploadResource()
  const useUploadMultipleResourcePayload = useUploadMultipleResource()
  const {
    callbacks: {
      launchResourceUploadModal,
    },
  } = useUploadResourcePayload
  const {
    callbacks: {
      launchMultipleResourceUploadModal,
    },
  } = useUploadMultipleResourcePayload

  const {
    callbacks: {
      updateServiceJobTotalAssets: updateFn,
      assignServiceJobStatus: createFn,
    },
    creating,
    loading: serviceJobLoading,
    showDuskPositional,
  } = useServiceJob({ id: serviceJob.id })

  const {
    callbacks: {
      upsertServiceJobDetail: createServiceJobDetailFn,
    },
  } = useServiceJobDetail()

  const { callbacks: { getServiceJobSetAvailable }, serviceJobSetAvailable, loading } = useServiceJobSetAvailable(serviceJob.id)
  const { callbacks: { postServiceJobPushRaw } } = useServiceJobPushRaw()
  const { callbacks: { postServiceJobSendToEsoft }, creating: sendToEsoftCreating } = useServiceJobSendToEsoft()
  const { currentUser } = useCurrentUser()

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceGroups, serviceJobs } = entities

  const duskJob = Object.values(serviceJobs).filter(x => ['Dusk Photography'].includes(x.service?.name) && x.campaign.id === serviceJob.campaign.id)[0]
  const launchMultipleUploader = ['Photography'].includes(serviceGroups[serviceJob.serviceGroupId]?.name) && (['BackOffice', 'ProductionManager'].includes(currentUser.userRole?.name) || currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'OnSite').id)

  const launchResourceUploader = () => {
    if (launchMultipleUploader) launchMultipleResourceUploadModal()
    else launchResourceUploadModal()

    toggleCheckActionStatus('upload-resources')
  }

  const updateTotalImages = (isDusk) => {
    swal.fire({
      title: isDusk ? 'Update Dusk Positional Images' : 'Update Total Images',
      html: `
        <style>
          .swal2-asset-grid{display:grid;width:100%;grid-template-columns:120px minmax(0,1fr) 14px;grid-row-gap:12px;grid-column-gap:10px;align-items:center;justify-items:stretch;}
          .swal2-asset-label{text-align:right;align-self:center;}
          .swal2-asset-label.note{align-self:start;padding-top:6px;}
          .swal2-asset-field{width:100%;min-width:0;display:flex;}
          .swal2-asset-input{width:100% !important;max-width:100% !important;box-sizing:border-box;display:block;height:36px;font-size:14px;line-height:1.4;padding:6px 10px;margin:0 !important;}
          .swal2-asset-textarea{width:100% !important;max-width:100% !important;box-sizing:border-box;display:block;min-height:110px;font-size:14px;line-height:1.4;padding:10px;margin:0 !important;}
          .swal2-asset-required{color:red;text-align:left;align-self:center;}
          .swal2-asset-required.note{align-self:start;padding-top:6px;}
        </style>
        <div class="swal2-asset-grid">
          <div class="swal2-asset-label">Total Images:</div>
          <div class="swal2-asset-field">
            <input type="number" id="totalAssets" class="swal2-input swal2-asset-input" value="${isDusk ? duskJob?.totalAssets : serviceJob.totalAssets}">
          </div>
          <div class="swal2-asset-required">*</div>
          <div class="swal2-asset-label note">Note:</div>
          <div class="swal2-asset-field">
            <textarea id="assetNote" class="swal2-input swal2-asset-textarea"></textarea>
          </div>
          <div class="swal2-asset-required note">*</div>
        </div>
      `,
      icon: 'info',
      showCancelButton: false,
      confirmButtonText: 'Confirm',
      showClass: 'slide-from-top',
      padding: '0.6rem',
      preConfirm: () => {
        const inputNumber = swal.getHtmlContainer().querySelector('#totalAssets').value
        const inputNote = swal.getHtmlContainer().querySelector('#assetNote').value
        if (!inputNote) swal.showValidationMessage('Note is required when updating Total Images.')
        if (!inputNumber) swal.showValidationMessage('Please input the total images.')

        const obj = { totalAssets: inputNumber, TotalAssetsNote: inputNote }
        updateFn(isDusk ? duskJob : serviceJob, obj).then(({ success }) => success)
      },
      allowOutsideClick: false,
    }).then((success) => {
      if (success) {
        launchResourceUploader()
      } else swal.showValidationMessage('Unable to update total images.')
    })
  }

  const proceedUploadResources = () => {
    if (!['Photography', 'Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)) {
      launchResourceUploader()
      return
    }

    if (['Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)) {
      siteplanConfirmation()
      return
    }

    confirmTotalImages()
  }

  const uploadResources = () => {
    const isStudioUser = currentUser?.userRole?.userTypeId === userTypes.find(x => x.name === 'Studio')?.id
    const isFloorplanStudioChangeRequest = ['Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name) && serviceJob.serviceJobStatusId === 160 && isStudioUser

    if (serviceJob.serviceJobStatusId >= 150 && !isFloorplanStudioChangeRequest) {
      infoPrompt()
      return
    }

    if (criticalPromptServices.includes(serviceJob.service?.id) && currentUser?.userRole?.userTypeId === 4) {
      promptCriticalIssue(serviceJob?.campaign?.criticalNote || '').then(({ shouldContinue, isCritical, criticalNote }) => {
        if (!shouldContinue) return

        if (isCritical) {
          const serviceJobParam = {
            id: serviceJob.id,
            serviceJob,
            payload: {
              isCritical: true,
              criticalNote,
            }
          }

          dispatch(patchServiceJobCritical(serviceJobParam, {}))
        }

        proceedUploadResources()
      })
      return
    }

    proceedUploadResources()
  }

  const confirmTotalImages = () => {
    swal.fire({
      title: 'Confirm Total Images',
      html: `This job requires ${serviceJob.totalAssets} images.  Please confirm if this is still correct.`,
      icon: 'question',
      showCancelButton: false,
      confirmButtonText: 'Yes',
      showDenyButton: true,
      denyButtonText: 'No',
      denyButtonColor: '#e2001a',
      showClass: 'slide-from-top',
      allowOutsideClick: false,
    }).then(({ value }) => {
      if (value && showDuskPositional) {
        confirmTotalDuskImages()
      } else if (value) {
        launchResourceUploader()
      } else {
        updateTotalImages(false)
      }
    })
  }

  const confirmTotalDuskImages = () => {
    swal.fire({
      title: 'Confirm Dusk Positional Total',
      html: `This job requires ${duskJob?.totalAssets} dusk positional images. Please confirm if this is still correct.`,
      icon: 'question',
      showCancelButton: false,
      confirmButtonText: 'Yes',
      showDenyButton: true,
      denyButtonText: 'No',
      denyButtonColor: '#e2001a',
      showClass: 'slide-from-top',
      allowOutsideClick: false,
    }).then(({ value }) => {
      if (value) launchResourceUploader()
      else updateTotalImages(true)
    })
  }


  const confirmResources = () => {
    swal.fire({
      title: 'Confirm Resources',
      html: 'Confirming will push the images to the customer. No further changes beyond this point.'
        + '<br/>Do you wish to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value) {
        const serviceJobParam = {
          id: serviceJob.id,
          serviceJobStatusId: 60,
          workflowComment: 'Confirm from CH-Resources',
        }
        assignServiceJobStatus(serviceJobParam, createFn)
        toggleCheckActionStatus('confirm-resources')
      }
    })
  }

  const sendToClient = () => {
    var text = portraitServices.find(x => x.id === serviceJob.service?.id) ? 'This action will send these images to the client for Portrait Selection and an email will be sent to notify them.' : 'This action will send these images to the client for Drone Selection and an email will be sent to notify them.'
    swal.fire({
      title: ' Resources',
      html: text
        + '<br/>Do you wish to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value) {
        const serviceJobParam = {
          id: serviceJob.id,
          serviceJobStatusId: 60,
          workflowComment: 'Send to Client from CH-Resources',
        }
        assignServiceJobStatus(serviceJobParam, createFn)
        toggleCheckActionStatus('confirm-resources')
      }
    })
  }

  const requestRaw = () => {
    var text = 'This action will send the selected images raw copies to Campaigntrack Studio.'
    swal.fire({
      title: 'Request RAW/JPG',
      html: text
        + '<br/>Do you wish to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value) {
        const serviceJobParam = {
          resources: selectedIds,
        }
        postServiceJobPushRaw(serviceJob.id, serviceJobParam)
        toggleCheckActionStatus('confirm-resources')
      }
    })
  }

  const sendToEsoft = () => {
    var text = 'This action will send the selected images raw copies to Esoft.'
    swal.fire({
      title: 'Send to Esoft',
      html: text
        + '<br/>Do you wish to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value) {
        const serviceJobParam = {
          resources: selectedIds,
        }
        postServiceJobSendToEsoft(serviceJob.id, serviceJobParam)
        toggleCheckActionStatus('confirm-resources')
      }
    })
  }

  const infoPrompt = () => {
    swal.fire({
      title: 'Job is already proofed',
      html: '<p>This job has already been proofed and additional resources will no longer be accommodated.</p>'
        + '<p>Please contact support@contenthouse.io so that they can assist you better.</p>',
      icon: 'info',
      confirmButtonText: 'Okay',
    })
  }

  const siteplanConfirmation = () => {
    swal.fire({
      title: 'Confirm Siteplan',
      html: '<p>Does this job have a site plan?</p>',
      icon: 'question',
      showCancelButton: false,
      confirmButtonText: 'Yes',
      showDenyButton: true,
      denyButtonText: 'No',
      denyButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      const siteplanPayload = {
        serviceJobId: serviceJob.id,
        fieldName: 'HasSiteplan',
        value: `{ "Value": ${value} }`,
      }

      upsertServiceJobDetail(siteplanPayload, createServiceJobDetailFn)

      launchResourceUploader()
    })
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (serviceJob.serviceJobStatusId === 60 || !prestigeServices.find(x => x.id === serviceJob.service?.id)) {
        clearInterval(interval)
      }
      else if (portraitServices.find(x => x.id === serviceJob.service?.id) || droneServices.find(x => x.id === serviceJob.service?.id)) {
        getServiceJobSetAvailable(serviceJob.id)
      } else { getServiceJobSetAvailable(serviceJob.id) }
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id
        && prestigeServices.find(x => x.id === serviceJob.service?.id)
        && (!serviceJobSetAvailable?.canConfirm || isMissingResources || serviceJob.serviceJobStatusId === 60)) && (
          <ListItem
            boxProps={{
              borderTop: '1px solid',
              padding: 'large',
              lineHeight: '1.3',
              alignItems: 'center',
            }}
            disableHover
          >
            <SidebarNoticeBox
              boxProps={{
                marginBottom: 'medium',
                backgroundColor: 'lightcyan!important',
                borderColor: 'rgb(125, 208, 215, 0.5)!important',
              }}
            >
              <SidebarNoticeBox.Title>
                {serviceJob.serviceJobStatusId === 60 ? 'Awaiting Selection' : (isMissingResources ? 'Current quantity on the Gallery does not match with the client order '
                  : 'Current resources does not match with Jobtrack')}
              </SidebarNoticeBox.Title>
              <SidebarNoticeBox.Paragraph>
                {serviceJob.serviceJobStatusId === 60 ? 'This job has already been confirmed.' : (isMissingResources ? 'Please review the client\'s order and try again.'
                  : 'Please wait for the syncing to be completed and try again.')}
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          </ListItem>
        )}
      {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id
        && (portraitServices.find(x => x.id === serviceJob.service?.id) || droneServices.find(x => x.id === serviceJob.service?.id))
        && (!serviceJobSetAvailable?.canConfirm)) && (
          <ListItem
            boxProps={{
              borderTop: '1px solid',
              padding: 'large',
              lineHeight: '1.3',
              alignItems: 'center',
            }}
            disableHover
          >
            <SidebarNoticeBox
              boxProps={{
                marginBottom: 'medium',
                backgroundColor: 'lightcyan!important',
                borderColor: 'rgb(125, 208, 215, 0.5)!important',
              }}
            >
              <SidebarNoticeBox.Title>
                {'Current resources does not match with Jobtrack'}
              </SidebarNoticeBox.Title>
              <SidebarNoticeBox.Paragraph>
                {'Please wait for the syncing to be completed and try again.'}
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          </ListItem>
        )}
      <ListItem
        boxProps={{
          borderTop: '1px solid',
          padding: 'large',
          lineHeight: '1.3',
          alignItems: 'center',
        }}
        flexDirection="column"
        style={{ cursor: 'pointer' }}
        disableHover
      >
        <Box flexDirection="row" alignItems="center" width="100%">
        <Box alignItems="center" marginRight="auto" justifyContent="flexStart" width="auto">
          <Button
            buttonStyle="secondary"
            icon={<FontAwesomeIcon icon={faCloudUpload} />}
            loading={serviceJobLoading}
            onClick={uploadResources}
            size="medium"
            disabled={disableUpload}
          >
            Upload Resources
          </Button>
        </Box>
        {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id && prestigeServices.find(x => x.id === serviceJob.service?.id)) && (
          <Box alignItems="center" marginLeft="medium" justifyContent="flexEnd" width="auto">
            <Button
              size="medium"
              buttonStyle="secondary"
              disabled={!serviceJobSetAvailable?.canConfirm || isMissingResources || serviceJob.serviceJobStatusId === 60}
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={confirmResources}
              loading={loading || creating}
            >
              Confirm
            </Button>
          </Box>
        )}
        {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id && (portraitServices.find(x => x.id === serviceJob.service?.id) || droneServices.find(x => x.id === serviceJob.service?.id))) && (
          <Box alignItems="center" marginLeft="medium" justifyContent="flexEnd" width="auto">
            <Button
              size="medium"
              buttonStyle="secondary"
              disabled={serviceJob.serviceJobStatusId >= 150 || !serviceJobSetAvailable?.canConfirm}
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={sendToClient}
              loading={loading || creating}
            >
              Send to Client Gallery
            </Button>
          </Box>
        )}
        {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id && (portraitServices.find(x => x.id === serviceJob.service?.id) || droneServices.find(x => x.id === serviceJob.service?.id))) && (
          <Box alignItems="center" marginLeft="medium" justifyContent="flexEnd" width="auto">
            <Button
              size="medium"
              buttonStyle="secondary"
              disabled={!serviceJobSetAvailable?.canConfirm}
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={requestRaw}
              loading={loading || creating}
            >
              Request RAW/JPG
            </Button>
          </Box>
        )}
        {(currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'Studio').id || currentUser?.userRole.userTypeId === userTypes.find(x => x.name === 'BackOffice').id) && (
          <Box alignItems="center" marginLeft="medium" justifyContent="flexEnd" width="auto">
            <Button
              size="medium"
              buttonStyle="secondary"
              disabled={serviceJob.serviceJobStatusId !== 50}
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={sendToEsoft}
              loading={sendToEsoftCreating}
            >
              Send to Esoft
            </Button>
          </Box>
        )}
        </Box>
        {disableUpload && (
          <Box marginTop="small" width="100%">
            <Text color="bodyFontLightColor" fontSize="xsmall" style={{ fontStyle: 'italic', width: '100%' }}>
              Uploading will be enabled once the provider checklist is completed.
            </Text>
          </Box>
        )}
      </ListItem>
    </>
  )
}

Footer.propTypes = {
  callbacks: PropTypes.object,
  disableUpload: PropTypes.bool,
  isMissingResources: PropTypes.bool,
}

export default Footer
